import { PlusOutlined, EditOutlined, DeleteOutlined, CopyOutlined } from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Button, Spin, message, Tooltip } from "antd";
import { doPost, doGet, doPatch, doDelete } from "../../../../../API/apis";
import { useParams } from "react-router-dom";
import MedicalCardsTitle from "../MedicalCardsTitle";
import Notification from "../../../../../components/Notification/Notification";
import FormatDateTime from "../../../../../components/FormatDate/FormatDateTime";
import showConfirmModal from "../../../../../components/ModalConfirm/ModalConfirm";
import LabelDisplay from "../../../../../components/LabelDisplay/LabelDisplay";
import { AddButton } from "../../../../../components/GlobalComponents/GlobalComponents";
import dayjs from "dayjs";

const FamilyHistory = ({showCopyAll}) => {
  const { id } = useParams();
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [visible, setVisible] = useState(false);
  const [editMode, setEditMode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [hoveredItemId, setHoveredItemId] = useState(null);
  const [familyHistoryVisible, setFamilyHistoryVisible] = useState(false);

  const openModal = () => {
    setVisible(true);
  };

  const handleCancel = () => {
    setVisible(false);
    setEditMode(false);
    setFamilyHistoryVisible(false);
    form.resetFields();
  };

  const handleText = (record) => {
    setSelectedItem(record);
    setFamilyHistoryVisible(true);
  };

  const handleCopy = (record) => {
    const textToCopy = `Family History: ${
      record.family_history || "N/A"
    }`

    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        message.success("Copied to clipboard");
      })
      .catch(() => {
        message.error("Failed to copy to clipboard");
      });
  };

  const handleCopyAll = () => {
    let textToCopy = "PATIENT FAMILY HISTORY\n";
    textToCopy += "=================\n\n";
    textToCopy += data.map(item => `Date: ${item.date_time ? dayjs(item.date_time).format("YYYY-MM-DD") : "N/A"}\nDescription: ${item?.family_history}`).join("\n\n");
    navigator.clipboard.writeText(textToCopy).then(() => {
      message.success("Copied to clipboard");
    }).catch(() => {
      message.error("Failed to copy to clipboard");     
    });
  };

    const handleDelete = (record) => {
    showConfirmModal({
      title: "Confirm Delete?",
      icon: null,
      content: "Are you sure you want to delete this?",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      className: "custom-modal",
      onOk() {
        deleteFamilyhistory(record);
      },
    });
  };

  const deleteFamilyhistory = async (record) => {
    try {
      const response = await doDelete(
        `/patient/delete-family-history/${id}/${record}/`
      );
      if (response.status === 200) {
        Notification.success("Family History deleted successfully");
        getFamilyHistory();
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleEdit = (record) => {
    setSelectedItem(record);
    setEditMode(true);
    form.setFieldsValue({
      family_history: record.family_history,
    });
    setVisible(true);
  };

  const handelFamilyHistory = async (values) => {
    setLoading(true);
    const data = {
      family_history: values.family_history,
    };
    try {
      const response = editMode
        ? await doPatch(
            `/patient/update-family-history/${id}/${selectedItem?.id}/`,
            data
          )
        : await doPost(`/patient/add-family-history/${id}/`, data);
      if (response.status === 200 || response.status === 201) {
        const successMessage = editMode
          ? "Family History updated successfully"
          : "Family History added successfully";
        Notification.success(successMessage);
        getFamilyHistory();
        handleCancel();
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getFamilyHistory = async () => {
    setLoading(true);
    try {
      const response = await doGet(`/patient/get-family-history/${id}/`);
      if (response.status === 200) {
        setData(response?.data.reverse());
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getFamilyHistory();
  }, []);

  return (
    <div>
      <div className="shadow-class p-4 border rounded-md  h-[180px]  max-h-[180px]">
        <div className="flex items-center justify-between">
          <MedicalCardsTitle title="Family History" />
          <div className="flex items-center gap-2">
            {showCopyAll && data && data.length > 0 && (
              <Tooltip title="Copy All">
                <CopyOutlined
                  className="text-[#1890ff] mr-2"
                  onClick={handleCopyAll}
                />
              </Tooltip>
            )}
          <AddButton openModal={openModal}/>
          </div>    
        </div>
        {loading ? (
          <div className="flex justify-center items-center">
            <Spin size="small" />
          </div>
        ) : (
          <div className="flex max-h-[130px]  overflow-auto mt-1">
            <ul>
              {data.map((item, index) => (
                <li
                  key={item.id}
                  className="text-[#1890ff] cursor-pointer flex justify-between items-center"
                  onMouseEnter={() => setHoveredItemId(item.id)}
                  onMouseLeave={() => setHoveredItemId(null)}
                >
                  <span
                    onClick={() => {
                      handleText(item);
                    }}
                    title={item?.family_history}
                  >
                    {index + 1}. {item?.family_history?.slice(0, 26)}
                    {item.family_history?.length > 26 ? "..." : ""}
                  </span>
                  {hoveredItemId === item.id && (
                    <div className="flex items-center ml-3">
                      <CopyOutlined
                        className="text-[#1890ff] mr-2"
                        onClick={() => {
                          handleCopy(item);
                        }}
                      />
                      <EditOutlined
                        className="text-[#1890ff] mr-2"
                        onClick={() => {
                          handleEdit(item);
                        }}
                      />
                      <DeleteOutlined
                        className="text-[#1890ff]"
                        onClick={() => {
                          handleDelete(item.id);
                        }}
                      />
                    </div>
                  )}
                </li>
              ))}
              {data.length === 0 && (
                <li className="flex justify-between items-center">
                  <span>No Family History</span>
                </li>
              )}
            </ul>
          </div>
        )}
      </div>

      <Modal
        title="Family History"
        open={visible}
        onCancel={handleCancel}
        footer={null}
        maskClosable={false}
        width={650}
        closeIcon={
          <Button size="small" className="app-close-icon" shape="round">
            Close
          </Button>
        }
      >
        <Form
          layout="vertical"
          name="family_history"
          form={form}
          onFinish={handelFamilyHistory}
        >
          <Form.Item name="family_history" label="Family History">
            <Input.TextArea rows={1} maxLength={500} showCount />
          </Form.Item>
          <div className="flex justify-end">
            <Button onClick={handleCancel}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              className="ml-2"
              loading={loading}
            >
              {editMode ? "Update" : "Add"}
            </Button>
          </div>
        </Form>
      </Modal>
      <Modal
        title="Family History"
        open={familyHistoryVisible}
        onCancel={handleCancel}
        closeIcon={
          <Button size="small" className="app-close-icon" shape="round">
            Close
          </Button>
        }
        footer={null}
        width={650}
      >
        {selectedItem && (
          <>
          <LabelDisplay title="Description" description={selectedItem?.family_history}/>
          <LabelDisplay title="Added By" description={selectedItem?.added_by}/>
          <LabelDisplay title="Date" description={<FormatDateTime date={selectedItem?.date_time} type="datetime" />}/>
          </>
        )}
      </Modal>
    </div>
  );
};

export default FamilyHistory;
